import { useEffect, useMemo, useState } from "react";

import type { Tracker } from "@features/api/TrackerApi";
import { getTrackerReadAt } from "@features/tracker/trackerReadWatermark";
import { hasUnreadByWatermark } from "./trackerUnreadDotLogic";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { pubsub } from "utils";
import { rootStore } from "@store/rootStore";
import { trackerQueryKeys } from "./queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { useTrackersPageMode } from "@/src/hooks/useTrackersPageMode";

function extractRows(payload: unknown): Tracker[] {
  const wrapper = payload as { data?: unknown } | undefined;
  const data = wrapper?.data as any;
  if (!data) {
    return [];
  }
  if (Array.isArray(data.rows)) {
    return data.rows;
  }
  if (data.data && Array.isArray(data.data.rows)) {
    return data.data.rows;
  }
  return [];
}

function extractModeRows(payload: unknown): Tracker[] {
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const record = payload as Record<string, unknown>;
  if ("unreadOnlyData" in record) {
    return extractRows(record.unreadOnlyData);
  }
  return [];
}

export function useTrackerTabUnreadDot() {
  const trackerMode = useTrackersPageMode();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const off = pubsub.on(pubSubKeyMap.TRACKER_READ_WATERMARK_UPDATED, () => {
      setVersion((v) => v + 1);
    });
    return () => off?.();
  }, []);

  // Tracker read realtime sync and initial snapshot are handled by the shared
  // notification sync coordinator (supabaseNotificationSyncCoordinator).
  // No separate Supabase channel needed here.

  return useMemo(() => {
    if (!ownerId) {
      return {
        hasUnreadDot: false,
        unreadTrackers: [] as Tracker[],
        unreadCount: 0,
      };
    }
    const modeListCache = queryClient.getQueryData(
      trackerQueryKeys.list({
        ownerId,
        limit: 10,
        unreadOnly: true,
        sortField: "last_triggered",
        sortOrder: "DESC",
      }),
    );
    const rows = extractModeRows(modeListCache);
    const unreadTrackers = rows.filter((row) =>
      hasUnreadByWatermark(ownerId, [row], (trackerId) =>
        getTrackerReadAt(ownerId, trackerId),
      ),
    );
    return {
      hasUnreadDot: unreadTrackers.length > 0,
      unreadTrackers,
      unreadCount: unreadTrackers.length,
    };
  }, [
    ownerId,
    queryClient,
    trackerMode.hasSignals,
    trackerMode.isFetching,
    trackerMode.isLoading,
    version,
  ]);
}
